import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import SellerRatingStars from './SellerRatingStars';
import ReportSellerModal from './ReportSellerModal';
import { useFrontendAuth } from '../hooks/useAuth';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';

interface SellerInfoPopoverProps {
    sellerId: string;
    sellerName: string;
    region?: string;
    averageRating?: number;
    ratingCount?: number;
    children: React.ReactNode;
}

type PanelPos = { top: number; left: number };

const PANEL_WIDTH = 320;
const GAP = 8;

function canHover(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

const SellerInfoPopover: React.FC<SellerInfoPopoverProps> = ({
    sellerId,
    sellerName,
    region,
    averageRating = 0,
    ratingCount = 0,
    children,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [pos, setPos] = useState<PanelPos>({ top: 0, left: 0 });
    const { user } = useFrontendAuth();
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    /** True when open was driven by hover — ignore the following click so it doesn't toggle closed */
    const openedByHoverRef = useRef(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    const updatePosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const panelH = panelRef.current?.offsetHeight ?? 220;
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceBelow < panelH + GAP && rect.top > spaceBelow;

        let top = openUp ? rect.top - panelH - GAP : rect.bottom + GAP;
        let left = rect.left;

        const maxLeft = window.innerWidth - PANEL_WIDTH - 12;
        left = Math.max(12, Math.min(left, maxLeft));
        top = Math.max(12, Math.min(top, window.innerHeight - panelH - 12));

        setPos({ top, left });
    }, []);

    const cancelCloseTimer = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const openPopover = useCallback((viaHover: boolean) => {
        cancelCloseTimer();
        if (viaHover) openedByHoverRef.current = true;
        setIsOpen(true);
    }, []);

    const closePopover = useCallback((immediate = false) => {
        cancelCloseTimer();
        openedByHoverRef.current = false;
        if (immediate) {
            setIsOpen(false);
            return;
        }
        timeoutRef.current = setTimeout(() => setIsOpen(false), 180);
    }, []);

    const handleTriggerClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        cancelCloseTimer();

        // Hover opened the panel; this click is the same gesture that often follows mouseenter — keep open
        if (openedByHoverRef.current && isOpen) {
            openedByHoverRef.current = false;
            return;
        }

        openedByHoverRef.current = false;
        setIsOpen((v) => !v);
    };

    const handleTriggerMouseEnter = () => {
        if (!canHover()) return;
        openPopover(true);
    };

    const handleTriggerMouseLeave = () => {
        if (!canHover()) return;
        closePopover(false);
    };

    const handlePanelMouseEnter = () => {
        if (!canHover()) return;
        cancelCloseTimer();
        setIsOpen(true);
    };

    const handlePanelMouseLeave = () => {
        if (!canHover()) return;
        closePopover(false);
    };

    useLayoutEffect(() => {
        if (!isOpen) return;
        updatePosition();
        const id = requestAnimationFrame(() => updatePosition());
        return () => cancelAnimationFrame(id);
    }, [isOpen, updatePosition]);

    useEffect(() => {
        if (!isOpen) return;
        const onScrollOrResize = () => updatePosition();
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [isOpen, updatePosition]);

    // Click outside closes (portaled panel is outside trigger DOM)
    useEffect(() => {
        if (!isOpen) return;
        const onPointerDown = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node;
            if (panelRef.current?.contains(target)) return;
            if (triggerRef.current?.contains(target)) return;
            if (rootRef.current?.contains(target)) return;
            closePopover(true);
        };
        // Defer so the opening click doesn't immediately close
        const id = window.setTimeout(() => {
            document.addEventListener('mousedown', onPointerDown);
            document.addEventListener('touchstart', onPointerDown);
        }, 0);
        return () => {
            window.clearTimeout(id);
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
        };
    }, [isOpen, closePopover]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    // Escape closes on mobile + keyboard (touch users have no hover-leave)
    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                closePopover(true);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, closePopover]);

    const isOwnProfile = user?.id === sellerId;
    const triggerAriaLabel = tr('seller.info.aria', 'Seller info: {name}').replace(
        '{name}',
        sellerName
    );

    const openReport = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        closePopover(true);
        // Open report after popover unmounts so stacking is clean
        requestAnimationFrame(() => setShowReportModal(true));
    };

    const panel = isOpen
        ? createPortal(
              <div
                  ref={panelRef}
                  role="dialog"
                  aria-label={sellerName}
                  className="fixed z-[9999] panel-edge-safe rounded-3xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[var(--bg-color)] p-5 shadow-2xl sm:w-80"
                  style={{ top: pos.top, left: pos.left }}
                  onMouseEnter={handlePanelMouseEnter}
                  onMouseLeave={handlePanelMouseLeave}
                  onClick={(e) => e.stopPropagation()}
              >
                  <div className="mb-4 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                          <p className="truncate text-lg font-semibold text-[var(--text-color)]">{sellerName}</p>
                          {region && (
                              <p className="mt-1 flex items-center gap-1 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                  {'\u{1F4CD}'} {region}
                              </p>
                          )}
                      </div>
                      <button
                          type="button"
                          onClick={() => closePopover(true)}
                          className="min-h-11 min-w-11 shrink-0 text-2xl leading-none text-[color-mix(in_srgb,var(--text-color),transparent_50%)] hover:text-[var(--text-color)]"
                          aria-label="Close"
                      >
                          {'\u2715'}
                      </button>
                  </div>

                  {averageRating > 0 && (
                      <div className="mb-4 flex items-center gap-3">
                          <SellerRatingStars averageRating={averageRating} ratingCount={ratingCount} size="sm" />
                      </div>
                  )}

                  <Link
                      to={`/users/${sellerId}`}
                      onClick={(e) => {
                          e.stopPropagation();
                          closePopover(true);
                      }}
                      className="mb-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--accent-color)]/25 py-3 text-sm font-semibold text-[var(--text-color)] no-underline transition-colors hover:bg-[var(--accent-color)]/40"
                  >
                      {tr('profile.view', 'View profile')}
                  </Link>

                  {!isOwnProfile && (
                      <button
                          type="button"
                          onClick={openReport}
                          className="flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                      >
                          {'\u{1F6A9}'} {t('component.reportseller.title')}
                      </button>
                  )}
              </div>,
              document.body
          )
        : null;

    return (
        <>
            <div ref={rootRef} className="relative block w-full max-w-full min-w-0">
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={handleTriggerClick}
                    onMouseEnter={handleTriggerMouseEnter}
                    onMouseLeave={handleTriggerMouseLeave}
                    aria-expanded={isOpen}
                    aria-haspopup="dialog"
                    aria-label={triggerAriaLabel}
                    className={`touch-target flex w-full min-w-0 max-w-full cursor-pointer items-center rounded-xl px-1 py-1 -ml-0.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-color)]/50 ${
                        isOpen
                            ? 'bg-[color-mix(in_srgb,var(--accent-color),transparent_82%)]'
                            : 'hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_88%)] active:bg-[color-mix(in_srgb,var(--accent-color),transparent_80%)]'
                    }`}
                >
                    {children}
                </button>
            </div>

            {panel}

            <ReportSellerModal
                sellerId={sellerId}
                sellerName={sellerName}
                isOpen={showReportModal}
                onClose={() => setShowReportModal(false)}
            />
        </>
    );
};

export default SellerInfoPopover;
